import { normalizeProjectBadgeChars, resolveProjectBadgeSwatch } from '@/shared/data/projectBadges';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import { alpha, darken } from '@/ui/primitives/colorUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ProjectDto } from '@flowqa/shared';

export type ProjectBadgeSize = 'default' | 'compact';

export const projectBadgeCompactSize = 20;

export const resolveProjectBadgeDisplayChars = (chars: string): string =>
  normalizeProjectBadgeChars(chars) || 'P';

export const resolveProjectBadgeSize = (size: ProjectBadgeSize): number | string =>
  size === 'compact' ? projectBadgeCompactSize : s('2xl');

export const buildProjectBadgeFrameSx = ({
  color,
  disabled,
  size,
}: {
  color: ProjectDto['badgeColor'];
  disabled: boolean;
  size: ProjectBadgeSize;
}): SxProps<Theme> => {
  const swatch = resolveProjectBadgeSwatch(color);

  return {
    width: resolveProjectBadgeSize(size),
    height: resolveProjectBadgeSize(size),
    borderRadius: r('sm'),
    backgroundColor: (theme: Theme) => {
      if (disabled) return theme.app.state.disabledBg;
      return isLightTheme(theme) ? alpha(swatch, 0.16) : swatch;
    },
    border: (theme: Theme) =>
      disabled
        ? `1px solid ${theme.app.colors.border}`
        : isLightTheme(theme)
          ? `1px solid ${alpha(swatch, 0.34)}`
          : '1px solid transparent',
    color: (theme: Theme) => {
      if (disabled) return theme.app.colors.textMuted;
      return isLightTheme(theme) ? darken(swatch, 0.42) : '#ffffff';
    },
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  } as const;
};

export const buildProjectBadgeTextSx = (compactText: boolean) =>
  ({
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    fontSize: `${
      compactText ? typographyTokens.captionSmall.fontSize : typographyTokens.body.fontSize
    } !important`,
    fontWeight: `${typographyTokens.label.fontWeight} !important`,
    color: 'inherit',
    textTransform: typographyTokens.captionSmall.textTransform,
  }) as const;
